import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ShieldAlert } from 'lucide-react';
import { useTerminalSession } from './session/useTerminalSession';
import SessionLobby from './session/SessionLobby';
import SessionRoom from './session/SessionRoom';
import './PublicSessionView.css';

// Sesión pública de videollamada de terminal (`/s/:space/:terminalId/:sessionId`). Contenedor
// delgado: llama al hook con toda la lógica y despacha a la vista según el estado. Partido de
// un componente de 1173 líneas de main (integración main→dev, epic B1).
export default function PublicSessionView() {
    const { space, terminalId, sessionId } = useParams();
    const navigate = useNavigate();
    const s = useTerminalSession(sessionId, space);

    if (s.loading) {
        return (
            <div className="psv-container psv-loading-center">
                <Loader2 size={44} className="psv-spinner" />
                <div style={{ marginTop: '16px', fontSize: '15px', color: '#9ca3af' }}>
                    Conectando a Terminal {terminalId}...
                </div>
            </div>
        );
    }

    if (s.error && !s.expired) {
        return (
            <div className="psv-container psv-loading-center">
                <div className="psv-expired-card">
                    <div className="psv-expired-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                        <AlertCircle size={36} />
                    </div>
                    <h2 className="psv-expired-title">No se pudo acceder a la sesión</h2>
                    <p className="psv-expired-text">{s.error}</p>
                    <button className="psv-btn-lobby" onClick={() => navigate('/')}>Ir al inicio</button>
                </div>
            </div>
        );
    }

    if (s.expired) {
        return (
            <div className="psv-container psv-loading-center">
                <div className="psv-expired-card">
                    <div className="psv-expired-icon">
                        <ShieldAlert size={36} />
                    </div>
                    <span className="psv-expired-badge">Sesión Caducada</span>
                    <h2 className="psv-expired-title">El tiempo de la reunión ha finalizado</h2>
                    <p className="psv-expired-text">
                        Esta sesión de videollamada pública en la Terminal {terminalId} estaba vinculada a una cita en el calendario que ya ha concluido.
                        El acceso al micrófono, cámara y comentarios ha sido cerrado automáticamente.
                    </p>
                    <button className="psv-btn-lobby" onClick={() => navigate('/')}>Cerrar terminal</button>
                </div>
            </div>
        );
    }

    if (!s.joined) {
        return (
            <SessionLobby
                space={space}
                terminalId={terminalId}
                localStream={s.localStream}
                micEnabled={s.micEnabled}
                camEnabled={s.camEnabled}
                participantName={s.participantName}
                setParticipantName={s.setParticipantName}
                joiningSession={s.joiningSession}
                lobbyVideoRef={s.lobbyVideoRef}
                onToggleMic={s.toggleLobbyMic}
                onToggleCam={s.toggleLobbyCam}
                onJoin={s.handleJoinSession}
            />
        );
    }

    return (
        <SessionRoom
            space={space}
            terminalId={terminalId}
            sessionId={sessionId}
            participantName={s.participantName}
            authUser={s.authUser}
            peers={s.peers}
            localStream={s.localStream}
            remoteStreams={s.remoteStreams}
            selectedSpeakerId={s.selectedSpeakerId}
            setSelectedSpeakerId={s.setSelectedSpeakerId}
            micEnabled={s.micEnabled}
            camEnabled={s.camEnabled}
            screenSharing={s.screenSharing}
            timeRemaining={s.timeRemaining}
            chatMessages={s.chatMessages}
            inputMessage={s.inputMessage}
            setInputMessage={s.setInputMessage}
            mainVideoRef={s.mainVideoRef}
            chatEndRef={s.chatEndRef}
            guestTokenRef={s.guestTokenRef}
            onToggleMic={s.toggleMic}
            onToggleCam={s.toggleCam}
            onToggleScreenShare={s.toggleScreenShare}
            onSendMessage={s.handleSendMessage}
            onLeave={s.handleLeaveCall}
        />
    );
}
